import React, { useState, useEffect, useRef } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useSelector } from 'react-redux';
import { mapDataForLineHighcharts } from '../../mappers/model/LineMapper';
import { Empty, Checkbox } from 'antd';

/**
 * A hook that returns the current window inner width.
 * It listens for resize events and updates the width value,
 * throttled by the specified interval (default 200ms).
 *
 * @param {number} throttleTime - Throttling time in milliseconds.
 * @returns {number} The current window inner width.
 */
function useWindowWidth(throttleTime = 200) {
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    let throttleTimeout = null;

    const handleResize = () => {
      if (throttleTimeout === null) {
        throttleTimeout = setTimeout(() => {
          setWidth(window.innerWidth);
          throttleTimeout = null;
        }, throttleTime);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    };
  }, [throttleTime]);

  return width;
}

const LineChartExample = ({
  statData,
  stat,
  showTitle = true,
  forceTitle = "",
  preComputedPlayerDataIsNotEmpty = true,
  showDataLabelToggle = false,
  chartIndex = 0,
  pageType = "team",
  height = '100%',
  showBodyWeightBaseline = false,
  showBodyWeightToggle = false,
  bodyWeightOption = false,
  setBodyWeightOption = () => {}
}) => {
  const {
    info: {
      team_colors: teamColors,
    },
  } = useSelector(({ team }) => team);

  // Create a storage key like "team_0", "team_1" or "player_0"
  const storageKey = `${pageType}_${chartIndex}`;
  // Initialize with a safer approach to accessing sessionStorage
  const getInitialDataLabelState = () => {
    if (typeof sessionStorage === 'undefined') {
      return false;
    }
    try {
      const savedValue = sessionStorage.getItem(storageKey);
      return savedValue === 'true';
    } catch (error) {
      console.error('Error accessing sessionStorage:', error);
      return false;
    }
  };

  // Inside your component function, add:
  const innerWidth = useWindowWidth(200); // updates every 200ms
  const containerWidth = innerWidth * 0.80;

  const isProfilePage = pageType === "profile";
  const [showDataLabels, setShowDataLabels] = useState(getInitialDataLabelState);
  const [showTrendlines, setShowTrendlines] = useState(false); // Default to false
  const [checkboxLeft, setCheckboxLeft] = useState(35); // Default position
  const chartRef = useRef(null);
  const chartComponentRef = useRef(null);
  const containerRef = useRef(null);
  const isMountedRef = useRef(true);

  // Calculate options first (before any other hooks use it)
  const options = React.useMemo(() => {
    if (preComputedPlayerDataIsNotEmpty) {
      // For profile page, we don't want to show data labels regardless of the toggle state
      const shouldShowDataLabels = isProfilePage ? false : showDataLabels;
      const baseOptions = mapDataForLineHighcharts(statData, teamColors, stat, showTitle, true, forceTitle, shouldShowDataLabels, pageType, showBodyWeightBaseline, showTrendlines);

      if (baseOptions && !isProfilePage) {
        baseOptions.chart = {
          ...baseOptions.chart,
          events: {
            ...baseOptions.chart?.events,
            load: function () {
              // Reset Highcharts color counters to ensure consistent colors
              this.colorCounter = 0;
              this.symbolCounter = 0;
              
              // Wait a bit for the chart to fully render before measuring
              setTimeout(() => {
                if (this && this.plotLeft && isMountedRef.current) {
                  setCheckboxLeft(this.plotLeft - 6);
                }
              }, 100);
            }
          }
        };
      }

      return baseOptions;
    }
    return null;
  }, [statData, teamColors, stat, preComputedPlayerDataIsNotEmpty, showDataLabels, forceTitle, showTitle, isProfilePage, pageType, showBodyWeightBaseline, showTrendlines]);

  // Update sessionStorage when state changes
  useEffect(() => {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    try {
      sessionStorage.setItem(storageKey, showDataLabels);
    } catch (error) {
      console.error('Error writing to sessionStorage:', error);
    }
  }, [showDataLabels, storageKey]);
  // Setup mounted ref for cleanup
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // For profile page, use Highcharts.chart directly like BarChart.jsx
  useEffect(() => {
    if (!isProfilePage) return; // Only apply this for profile pages

    // Destroy the chart if it already exists to avoid conflicts
    if (chartRef.current) {
      chartRef.current.destroy();
    }

    // Reinitialize the chart with updated options
    if (containerRef.current && options) {
      // Add chart load event to ensure proper positioning
      const chartOptions = {
        ...options,
        chart: {
          ...options.chart,
          height: height,
          events: {
            ...options.chart?.events,
            load: function () {
              // Reset Highcharts color counters to ensure consistent colors
              this.colorCounter = 0;
              this.symbolCounter = 0;
              
              // Wait a bit for the chart to fully render before measuring
              setTimeout(() => {
                if (this && this.plotLeft && isMountedRef.current) {
                  setCheckboxLeft(this.plotLeft - 6);
                }
              }, 100);
            }
          }
        }
      };

      chartRef.current = Highcharts.chart(containerRef.current, chartOptions);
    }
  }, [options, height, isProfilePage]);

  // For team/player pages, use the original approach with chart instance
  useEffect(() => {
    if (isProfilePage) return; // Skip for profile pages

    // Access chart instance after component has mounted
    const chartInstance = chartComponentRef.current?.chart;

    if (chartInstance) {
      // Define a function to update checkbox position
      const updateCheckboxPosition = () => {
        if (chartInstance && chartInstance.yAxis && chartInstance.yAxis[0] &&
          typeof chartInstance.plotLeft === 'number' && isMountedRef.current) {
          setCheckboxLeft(chartInstance.plotLeft - 6);
        }
      };

      // One-time setup of render callback
      const handleRender = function () {
        updateCheckboxPosition();
      };

      // Add event listener using Highcharts event API
      Highcharts.addEvent(chartInstance, 'render', handleRender);

      // Add window resize listener to update position on window resize
      const handleResize = () => {
        if (chartInstance && !chartInstance.isResizing) {
          updateCheckboxPosition();
        }
      };

      window.addEventListener('resize', handleResize);

      // Run once initially to set position
      updateCheckboxPosition();

      // Cleanup function to remove event listeners when component unmounts
      return () => {
        if (chartInstance && Highcharts.removeEvent) {
          Highcharts.removeEvent(chartInstance, 'render', handleRender);
        }
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isProfilePage]);

  if (!options || !preComputedPlayerDataIsNotEmpty) {
    return (
      <div style={{
        height: isProfilePage ? height : '400px',
        width: isProfilePage ? '100%' : containerWidth,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Empty description={forceTitle ? "No data for '" + forceTitle + "' in the specified date range" : "No data in the specified date range"} />
      </div>
    );
  }

  // Use different rendering approaches based on page type
  if (isProfilePage) {
    return (
      <div ref={containerRef} style={{ width: '100%', height }} />
    );
  } else {
    // Original rendering for team/player pages
    return (
      <div style={{ position: 'relative' }} ref={containerRef}>
        {showDataLabelToggle && (
          <div style={{
            position: 'absolute',
            top: '10px',
            left: `${checkboxLeft}px`,
            zIndex: 10,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            padding: '2px 6px',
            borderRadius: '3px'
          }}>
            <Checkbox
              checked={showDataLabels}
              onChange={(e) => setShowDataLabels(e.target.checked)}
              style={{ color: '#888888' }}
            >
              Show labels
            </Checkbox>
          </div>
        )}
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '160px',
          zIndex: 10,
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          padding: '2px 6px',
          borderRadius: '3px'
        }}>
          <Checkbox
            checked={showTrendlines}
            onChange={(e) => setShowTrendlines(e.target.checked)}
            style={{ color: '#888888' }}
          >
            Show trendlines
          </Checkbox>
        </div>
        {showBodyWeightToggle && <div style={{
          position: 'absolute',
          top: '10px',
          left: `${checkboxLeft+140}px`,
          zIndex: 10,
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          padding: '2px 6px',
          borderRadius: '3px'
        }}>
          <Checkbox
            checked={bodyWeightOption}
            onChange={(e) => setBodyWeightOption(e.target.checked)}
            style={{ color: '#888888' }}
          >
            Show Body Weight Baseline
          </Checkbox>
        </div>
        }
        <HighchartsReact
          containerProps={{ style: { height: '400px', width: containerWidth } }}
          highcharts={Highcharts}
          options={options}
          ref={chartComponentRef}
        />
      </div>
    );
  }
};

export default LineChartExample;